import React from 'react'

// Reference doc for the anomalies dashboard. All sections collapsed by
// default; the operator opens what they need. Kept inline (not a markdown
// loader) so we can render real tables and tweak copy without a build step.

const Section = (props: { title: string; children: React.ReactNode }) => (
  <details className="anomalies-faq-section">
    <summary className="anomalies-faq-summary">
      <span className="anomalies-faq-title">{props.title}</span>
      <span className="anomalies-faq-chevron">▸</span>
    </summary>
    <div className="anomalies-faq-body">{props.children}</div>
  </details>
)

const Group = (props: { title: string; children: React.ReactNode }) => (
  <div className="anomalies-faq-group">
    <div className="anomalies-faq-group-title">{props.title}</div>
    {props.children}
  </div>
)

export const Faq = () => (
  <div className="anomalies-faq">
    <Group title="The basics">
      <Section title="What does the detector do?">
        <p>
          For every advertiser, every day, separately for Mobile and Desktop,
          we ask: <b>"did something go meaningfully wrong with this partner
          today?"</b>
        </p>
        <p>If yes, we raise an <b>alert</b> with:</p>
        <ul>
          <li>A category (drift / acute / rejection)</li>
          <li>A primary metric (CR / EPC / SPC / clicks / conversions / rejection rate)</li>
          <li>A priority score (how much we think it matters)</li>
          <li>An estimated commercial impact (in DKK / lost conversions)</li>
        </ul>
      </Section>

      <Section title="What is the grain?">
        <p>
          One row per <b>(advertiser, device, day)</b>. Mobile and Desktop are
          tracked independently — an issue can fire on Mobile without
          affecting Desktop, and vice versa.
        </p>
        <p>
          We don't break alerts down by network — we aggregate across
          networks. But the dashboard shows which networks were active that
          day so you can investigate.
        </p>
      </Section>

      <Section title="How often is the data refreshed?">
        <p>
          Once per day. The data you see is yesterday's full day. There's no
          real-time / intra-day detection.
        </p>
      </Section>
    </Group>

    <Group title="The three alert families">
      <Section title="Acute drop">
        <p>
          <b>Definition:</b> Something dropped suddenly in the last 7 days
          compared to the prior 30 or 90 days.
        </p>
        <p>
          <b>How it fires:</b> The recent 7-day rate must be both
        </p>
        <ul>
          <li>statistically unusual (z-score below threshold vs. baseline volatility), and</li>
          <li>materially lower in absolute terms (e.g. CR down more than ~15%)</li>
        </ul>
        <p>
          Both gates must agree — z-score alone gives too many false positives
          on low-volume partners; absolute drop alone gives false positives on
          naturally volatile partners.
        </p>
        <p><b>Metrics it covers:</b> CR, EPC, SPC, clicks, conversions</p>
        <p>
          <b>Example:</b> "Mobile CR was 4.2% last 30 days. This week it's
          2.1%. The drop is 3 standard deviations below normal weekly
          variation." → acute CR drop.
        </p>
      </Section>

      <Section title="Drift">
        <p>
          <b>Definition:</b> The current 30-day rate is materially below the
          partner's <b>long-term historical anchor</b> (180 days prior), and
          the recent trajectory confirms it hasn't recovered.
        </p>
        <p><b>How it fires:</b> Two gates must both pass:</p>
        <ol>
          <li>
            <b>Anchor gate</b> — current 30d ÷ 180d anchor ≤ 0.70 (i.e. ≥30%
            below historical normal)
          </li>
          <li>
            <b>Recent gate</b> — recent 7d ÷ 30d ≤ 0.85, OR the anchor ratio
            is severely depressed (≤ 0.60)
          </li>
        </ol>
        <p>
          Why two gates: the anchor catches gradual decline that acute misses
          (acute compares 7d to 30d — but if the 30d itself has been quietly
          dropping for months, acute sees nothing wrong). The recent gate
          filters out "the partner already recovered weeks ago, this is
          stale."
        </p>
        <p>
          Drift also has commercial-value gates — we don't fire drift on a
          partner that has nearly no commercial activity, even if their CR
          collapsed in percent terms.
        </p>
        <p><b>Metrics it covers:</b> CR, EPC, SPC</p>
        <p>
          <b>Example:</b> "Boozt Mobile CR was 3.8% averaged across mid-2024.
          Now it's 2.4% over the last 30 days. Even adjusting for seasonality,
          the partner is operating 37% below long-term normal." → CR drift.
        </p>
      </Section>

      <Section title="Rejection spike">
        <p>
          <b>Definition:</b> The rate at which conversions are getting
          rejected has materially jumped.
        </p>
        <p>
          <b>How it fires:</b> Recent 7-day rejection rate is significantly
          above the 30d or 90d baseline. Has its own absolute floor — won't
          fire on partners with 1 rejection out of 4 conversions.
        </p>
        <p>
          <b>Example:</b> "Adtraction reported rejecting 12% of last week's
          conversions; the prior month average was 4%." → rejection spike.
        </p>
      </Section>
    </Group>

    <Group title="What do the metrics mean?">
      <Section title="Metric definitions">
        <table className="anomalies-faq-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Definition</th>
              <th>What it tells you</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>CR</b> (conversion rate)</td>
              <td>conversions ÷ clicks</td>
              <td>Tracking funnel health — are clicks converting?</td>
            </tr>
            <tr>
              <td><b>EPC</b> (earnings per click)</td>
              <td>commission_dkk ÷ clicks</td>
              <td>
                Our monetization yield. Drops when CR falls, commission
                falls, or both.
              </td>
            </tr>
            <tr>
              <td><b>SPC</b> (sale per click)</td>
              <td>sale_dkk ÷ clicks</td>
              <td>
                Order-value-per-click. Useful for catching basket-size
                shifts independent of CR.
              </td>
            </tr>
            <tr>
              <td><b>Rejection rate</b></td>
              <td>rejected ÷ conversions</td>
              <td>
                How many tracked conversions get clawed back. Spikes
                indicate fraud, attribution disputes, or tracking issues.
              </td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section title="Why three yield metrics (CR, EPC, SPC)?">
        <p>They isolate different failure modes:</p>
        <ul>
          <li>
            <b>CR drops, EPC/SPC stable</b> → tracking issue. Conversions are
            getting lost.
          </li>
          <li>
            <b>CR stable, EPC drops</b> → commission rate cut. We're getting
            paid less per sale.
          </li>
          <li>
            <b>CR + EPC stable, SPC drops</b> → basket size shrinking.
            Conversions are happening but smaller.
          </li>
        </ul>
        <p>Each metric drifts/acutely-drops independently.</p>
      </Section>
    </Group>

    <Group title="Dashboard concepts">
      <Section title="Active alerts">
        <p>
          The count of alert rows for the selected date. One row = one
          (advertiser, device, day) that fired. An advertiser can contribute
          up to 2 alerts on a single day (Mobile + Desktop).
        </p>
      </Section>
      <Section title="New alerts">
        <p>
          <code>is_first_alert_day = 1</code> — the partner did not have an
          alert in the same family on the previous day. This is the "fresh
          problem" signal — if you only watched one column, watch this one.
        </p>
      </Section>
      <Section title="Worsening alerts">
        <p>
          <code>is_worsening_today = 1</code> — the partner already had this
          alert yesterday, but the primary metric got materially worse today.
          Catches deteriorating-but-not-new situations.
        </p>
      </Section>
      <Section title="Continuing alerts">
        <p>
          Neither new nor worsening — the alert is the same as yesterday, not
          getting worse. Usually safe to leave for a few days while you focus
          on new/worsening.
        </p>
      </Section>
      <Section title="First-seen date / days-active">
        <p>
          For each ongoing alert episode, we track when it first fired
          (<code>alert_first_seen_date</code>) and how many days it's been
          continuously active (<code>alert_days_active</code>). Useful for
          "this drift has been going on for 47 days."
        </p>
      </Section>
      <Section title="Priority score (action_priority_rank)">
        <p>Each alert gets a priority score combining:</p>
        <ul>
          <li>
            <b>severity_score</b> — how unusual the metric is (z-score
            magnitude, % below anchor, etc.)
          </li>
          <li>
            <b>commercial_weight</b> — how much money is on the line
            (commission volume, sales volume)
          </li>
          <li>A seasonal dampening factor (0.5x for low-severity seasonal alerts)</li>
        </ul>
        <p>
          This is what the triage list is sorted by. High = look first.
        </p>
      </Section>
    </Group>

    <Group title="Baselines — what gets compared to what">
      <Section title="Window types">
        <table className="anomalies-faq-table">
          <thead>
            <tr>
              <th>Baseline</th>
              <th>Window</th>
              <th>Used for</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>7d</code></td>
              <td>Last 7 days</td>
              <td>"what's happening now"</td>
              <td>The recent state we evaluate</td>
            </tr>
            <tr>
              <td><code>30d_base</code></td>
              <td>Days -37 to -8</td>
              <td>Acute drop comparison</td>
              <td>Excludes the recent window</td>
            </tr>
            <tr>
              <td><code>90d_base</code></td>
              <td>Days -97 to -8</td>
              <td>Acute drop comparison (longer)</td>
              <td>Less noisy than 30d_base</td>
            </tr>
            <tr>
              <td><code>30d_curr</code></td>
              <td>Last 30 days</td>
              <td>Drift current-period</td>
              <td>Includes the recent 7</td>
            </tr>
            <tr>
              <td><code>180d_anchor</code></td>
              <td>Prior 180 days (excl. recent 30)</td>
              <td><b>Drift comparison</b></td>
              <td>Long-term historical normal</td>
            </tr>
            <tr>
              <td><code>nonseasonal</code></td>
              <td>Anchor excl. Nov 15 – Jan 15</td>
              <td>Q4 guard</td>
              <td>See "Q4" section</td>
            </tr>
            <tr>
              <td><code>stable_anchor</code></td>
              <td>P75 of past 6-month windows</td>
              <td>Severity scoring</td>
              <td>The partner's "typical good state"</td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section title="Why so many windows?">
        <ul>
          <li>
            <b>7v30 / 7v90</b> — recent vs. recent-ish. Catches acute drops.
          </li>
          <li>
            <b>30v180 anchor</b> — current-month vs. long-term. Catches drift
            that's been creeping for months.
          </li>
          <li>
            <b>stable_anchor</b> — what's the partner's "good state" — used
            to estimate impact.
          </li>
        </ul>
      </Section>

      <Section title="Rates are computed from totals, not averages">
        <p>
          All rates (CR, EPC, SPC) are computed as{' '}
          <code>SUM(numerator) ÷ SUM(denominator)</code> over the window —
          NOT <code>AVG(daily_rate)</code>. This is important: a partner with
          999 clicks no conversions and 1 click 1 conversion has a CR of
          1/1000 = 0.1%, not (0% + 100%) / 2 = 50%.
        </p>
      </Section>
    </Group>

    <Group title="Q4 and seasonality">
      <Section title="Why Q4 is special">
        <p>
          Most affiliate partners see massive volume spikes around Black
          Friday, Cyber Monday, and Christmas. CR / EPC / SPC also typically
          rise (higher purchase intent). This causes two problems for naive
          anomaly detection:
        </p>
        <ol>
          <li>
            <b>False alarms in Q4</b> — "CR jumped 60% week-over-week!" — yes,
            it's Black Friday.
          </li>
          <li>
            <b>False alarms post-Q4</b> — "CR dropped 40% vs. the 90-day
            baseline!" — yes, the 90-day baseline still includes Black Friday.
          </li>
        </ol>
      </Section>

      <Section title="How we handle it">
        <p>
          <b>is_seasonal flag</b> — set if the advertiser typically sees high
          traffic in the current period. Used to dampen priority score for
          non-severe alerts (so seasonal noise doesn't drown out real
          signals).
        </p>
        <p>
          <b>Non-seasonal baseline</b> — we compute a parallel set of
          long-term baselines that explicitly exclude Nov 15 – Jan 15 from
          the historical window. When the regular anchor would be
          Q4-contaminated, we compare against this clean version.
        </p>
        <p>
          <b>Q4 anchor-inflated flags</b> — if the partner's 180-day anchor
          has ≥ 14 days of Q4 in it, we flag it
          (<code>q4_anchor_inflated_cr</code>, etc.).
        </p>
        <p>
          <b>Post-peak guard</b> — if a drift alert would fire AND the anchor
          is Q4-inflated AND the partner is still operating at ≥ 90% of their
          non-seasonal baseline, we <b>suppress</b> the alert. This is the
          "Q4 normalization isn't a drift" filter.
        </p>
        <p>
          The dashboard should de-emphasize alerts where any{' '}
          <code>post_peak_guard_*</code> is set — they exist for transparency
          but didn't actually fire.
        </p>
      </Section>

      <Section title="Commission-missing exception">
        <p>
          If commission is reporting as zero while sales and conversions look
          normal, we bypass the EPC Q4 guard — that's a tracking/payout
          problem, not seasonality.
        </p>
      </Section>
    </Group>

    <Group title="Impact estimation">
      <Section title="What we estimate">
        <p>
          We estimate the commercial impact of each alert based on the gap
          between current and "good state" performance, scaled to a 30-day
          window. Three estimates:
        </p>
        <table className="anomalies-faq-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Definition</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>est_lost_conversions_30d</code></td>
              <td>
                If the partner had performed at their stable anchor instead,
                how many more conversions would we have seen in the last 30
                days?
              </td>
            </tr>
            <tr>
              <td><code>est_lost_commission_30d_dkk</code></td>
              <td>Same idea, in DKK commission lost</td>
            </tr>
            <tr>
              <td><code>est_lost_sale_30d_dkk</code></td>
              <td>Same idea, in DKK gross sale lost</td>
            </tr>
          </tbody>
        </table>
        <p>Plus two CR-specific decompositions:</p>
        <ul>
          <li>
            <code>est_lost_sale_from_cr_dkk</code> — sale lost specifically
            due to CR being below anchor (holding conversion volume constant)
          </li>
          <li>
            <code>est_lost_commission_from_cr_dkk</code> — same but commission
          </li>
        </ul>
      </Section>

      <Section title="How does the dashboard pick which to show?">
        <p>Depends on the alert family + metric:</p>
        <ul>
          <li><b>CR drift</b> → "Est. N lost conversions"</li>
          <li><b>EPC drift</b> → "Est. N DKK lost commission"</li>
          <li><b>SPC drift</b> → "Est. N DKK lost sale"</li>
          <li><b>Acute</b> → no impact label (the alert is about volatility, not loss)</li>
        </ul>
      </Section>

      <Section title="Why no impact for acute alerts?">
        <p>
          Acute drops by definition are short-term. A 7-day window doesn't
          tell us whether the partner has lost X DKK over 30 days — we don't
          extrapolate.
        </p>
      </Section>
    </Group>

    <Group title="What we deliberately don't detect">
      <Section title="Known blind spots">
        <p>Knowing the limits matters.</p>
        <table className="anomalies-faq-table">
          <thead>
            <tr>
              <th>Case</th>
              <th>Why not</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>Disappeared advertisers</b></td>
              <td>
                If a partner just stops sending data, we have no rows →
                nothing to alert on. By design — preventing this requires a
                separate "expected-but-missing" check we haven't built.
              </td>
            </tr>
            <tr>
              <td><b>First-day partners</b></td>
              <td>
                Minimum activity threshold:{' '}
                <code>active_days_90d ≥ 21</code>. Brand-new partners are
                silent until they have enough history.
              </td>
            </tr>
            <tr>
              <td><b>Single-network issues</b></td>
              <td>
                We aggregate across all networks the advertiser uses. If
                Awin breaks but Adtraction is healthy, the totals may absorb
                the issue. The dashboard shows per-day networks so you can
                spot this manually.
              </td>
            </tr>
            <tr>
              <td><b>Network-level alerts</b></td>
              <td>Same as above — alerts are advertiser-level.</td>
            </tr>
            <tr>
              <td><b>Sub-day issues</b></td>
              <td>
                Daily granularity. A mid-day tracking outage that recovers
                same day won't alert.
              </td>
            </tr>
            <tr>
              <td><b>Reversed issues (recovery)</b></td>
              <td>
                We don't fire "the partner recovered!" alerts. The alert
                just stops appearing.
              </td>
            </tr>
            <tr>
              <td><b>Acks / suppression / workflow</b></td>
              <td>
                No "I've seen this, stop showing me" feature in the current
                version.
              </td>
            </tr>
          </tbody>
        </table>
      </Section>
    </Group>

    <Group title="More details">
      <Section title="Multiple alerts on the same day">
        <p>
          A partner × device × day has <b>one</b> alert classification — one{' '}
          <code>alert_family</code> and one <code>primary_metric</code>. If
          multiple conditions fire simultaneously (e.g., both CR drift AND
          EPC drift), we pick the highest-priority one and list everything
          firing in <code>diagnostic_alert_reasons</code> (semicolon-
          separated).
        </p>
        <p>
          So the dashboard headline tells you the most important issue, and
          the reason text tells you what else is firing.
        </p>
        <p>
          If both Mobile and Desktop alert, you'll see two separate rows in
          the triage list (one per device).
        </p>
      </Section>

      <Section title="Maturity gates">
        <p>To avoid alerting on noise from low-data partners, we require:</p>
        <ul>
          <li>
            <code>active_days_90d ≥ 21</code> — at least 21 active days in
            the last 90
          </li>
          <li>
            <code>active_days_180d ≥ 60</code> — at least 60 active days in
            the last 180 (for drift, which needs historical anchor)
          </li>
          <li>
            Each metric has its own minimum-volume floor for the relevant
            window
          </li>
        </ul>
        <p>
          Partners that fail these aren't shown — they're not "no alerts,"
          they're "we don't have enough signal to say."
        </p>
      </Section>

      <Section title="Priority score, severity, and colors">
        <p>
          <b>Severity score</b> captures how unusual the deviation is:
        </p>
        <ul>
          <li>Multi-component for acute (z-score magnitude + relative drop)</li>
          <li>Anchor ratio depth for drift (the further below anchor, the higher)</li>
          <li>For rejection, the absolute z above baseline</li>
        </ul>
        <p>
          <b>Commercial weight</b> captures how much money is at stake:
        </p>
        <ul>
          <li>Clicks volume, commission volume, sale volume</li>
          <li>
            Normalized so a small partner with proportionally big problem
            doesn't outrank a huge partner with a moderate problem (but
            isn't completely buried either)
          </li>
        </ul>
        <p>
          <b>Final priority</b> = severity × commercial weight × seasonal
          dampening. Seasonal dampening is 0.5x <b>only when</b> the alert is
          below a severity floor AND is on a seasonal partner AND isn't a
          rejection or volume drop. Severe seasonal alerts are NOT dampened.
        </p>
      </Section>

      <Section title='"Why isn&apos;t X alerting?"'>
        <p>Common reasons a partner you expect to alert isn't on the list:</p>
        <ol>
          <li>
            <b>Maturity gates</b> — &lt; 21 active days in 90d, or &lt; 60 in
            180d (drift needs longer history)
          </li>
          <li>
            <b>Below volume floor</b> — not enough clicks/conversions in the
            relevant window to be statistically meaningful
          </li>
          <li>
            <b>Post-peak guard fired</b> — the dip is being suppressed as Q4
            normalization (check <code>post_peak_guard_*</code> in
            detector_daily)
          </li>
          <li>
            <b>Seasonal dampening</b> — alert exists but priority is halved →
            buried lower in the list
          </li>
          <li>
            <b>Only one device matters</b> — Mobile may be alerting; Desktop
            you're looking at isn't
          </li>
          <li>
            <b>Recovery</b> — the partner was alerting yesterday, but today's
            7-day window now includes the recovered days, so the alert lapsed
          </li>
          <li>
            <b>Network-level issue masked at advertiser level</b> — one bad
            network is being absorbed by other healthy networks
          </li>
        </ol>
      </Section>
    </Group>

    <Group title="Glossary">
      <Section title="Terms">
        <table className="anomalies-faq-table">
          <thead>
            <tr><th>Term</th><th>Definition</th></tr>
          </thead>
          <tbody>
            <tr><td><b>Advertiser</b></td><td>A retailer (matas.dk, boozt.com, etc.)</td></tr>
            <tr><td><b>Network</b></td><td>Affiliate platform (Adtraction, Awin, Rakuten, etc.)</td></tr>
            <tr><td><b>Tracker</b></td><td>A specific advertiser × network combination</td></tr>
            <tr><td><b>Acute</b></td><td>Sudden short-term drop (7d vs 30d/90d)</td></tr>
            <tr><td><b>Drift</b></td><td>Gradual long-term decline (30d vs 180d anchor)</td></tr>
            <tr><td><b>Anchor</b></td><td>Long-term historical baseline (180-day window prior to recent 30)</td></tr>
            <tr><td><b>Stable anchor</b></td><td>P75 of past 6-month rolling windows</td></tr>
            <tr><td><b>Non-seasonal baseline</b></td><td>Anchor that excludes Nov 15 – Jan 15</td></tr>
            <tr><td><b>CR</b></td><td>Conversion rate (conversions / clicks)</td></tr>
            <tr><td><b>EPC</b></td><td>Earnings per click (commission / clicks)</td></tr>
            <tr><td><b>SPC</b></td><td>Sale per click (sale_dkk / clicks)</td></tr>
            <tr><td><b>Rejection rate</b></td><td>rejected_conversions / conversions</td></tr>
            <tr><td><b>Z-score</b></td><td>How many standard deviations from baseline</td></tr>
            <tr><td><b>Q4 inflated</b></td><td>The historical anchor includes ≥14 days of Q4 traffic</td></tr>
            <tr><td><b>Post-peak guard</b></td><td>A would-be drift alert was suppressed because the dip is normal Q4 normalization</td></tr>
            <tr><td><b>Active alert</b></td><td>Any alert row for the selected day</td></tr>
            <tr><td><b>New alert</b></td><td>First day this partner has fired in this family</td></tr>
            <tr><td><b>Worsening alert</b></td><td>Partner already alerting, got materially worse today</td></tr>
            <tr><td><b>Days-active</b></td><td>Consecutive days this alert episode has been firing</td></tr>
          </tbody>
        </table>
      </Section>
    </Group>
  </div>
)
